import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAdminCaseDetail,
  getSignedVideoUrl,
  upsertObservations,
  markCaseAnalyzing,
  runResearch,
  regenerateIllustrations,
  finalizeReport,
  backfillVideoThumbnail,
} from "../lib/admin.js";
import { ReportView } from "../parent/screens.jsx";
import { SYMPTOM_GROUPS, SYMPTOM_LABEL_MAP } from "../lib/symptoms.js";
import SimilarCases from "./SimilarCases.jsx";

// SYMPTOM_GROUPS / SYMPTOM_LABEL_MAP은 lib/symptoms.js에서 import. 같은 매핑이
// api/research-case.ts (SYMPTOM_LABELS) 및 api/lib/pubmed.ts (SYMPTOM_QUERY)와
// 동기화되어 유지된다.

const STATUS_LABEL = {
  draft: "작성 중",
  received: "신규 · 우선",
  analyzing: "분석 중",
  research: "AI 리서치 중",
  reviewing: "검토 대기",
  done: "전송 완료",
  cancelled: "취소됨",
};

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months =
    (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

function formatDuration(seconds) {
  if (seconds == null) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function shortId(id) {
  return id ? "C-" + id.slice(0, 6).toUpperCase() : "";
}

export default function AdminWorkspace({ caseId, operatorId, onBack, onSubmit, onSignOut }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [symptoms, setSymptoms] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [activeVideoIdx, setActiveVideoIdx] = useState(0);
  const [signedUrl, setSignedUrl] = useState(null);
  const [videoError, setVideoError] = useState(null);
  // Player container reshapes to match the actual video aspect once metadata
  // loads — fixed 16/10 produced huge letterbox bars for portrait clips.
  const [playerAspect, setPlayerAspect] = useState({ ratio: "16 / 10", value: 1.6 });
  // videoId → signed URL for the thumbnail object (parallel to signedUrl above)
  const [thumbUrls, setThumbUrls] = useState({});

  const [aiStatus, setAiStatus] = useState("idle"); // 'idle' | 'running' | 'done'
  // True when symptoms/notes have changed since the last AI research run —
  // the existing letter/papers/recommendations no longer reflect the inputs.
  const [inputsStale, setInputsStale] = useState(false);
  const [papersFound, setPapersFound] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [letterDraft, setLetterDraft] = useState("");
  const [letterDirty, setLetterDirty] = useState(false);

  // D-2: papers/recommendations/observations 편집 가능 (각각 dirty 추적)
  const [papersDraft, setPapersDraft] = useState([]);
  const [papersDirty, setPapersDirty] = useState(false);
  const [recsDraft, setRecsDraft] = useState([]);
  const [recsDirty, setRecsDirty] = useState(false);
  const [obsDraft, setObsDraft] = useState([]);
  const [obsHeadline, setObsHeadline] = useState("");
  const [obsDirty, setObsDirty] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  // 인포그래픽 — research row의 illustrations를 그대로 들고 다닌다. operator는
  // 개별 편집 대신 "재생성" 한 번에 전체 갈아끼움. 정상 발행될 때 그대로
  // it_reports.content.illustrations로 전달된다.
  const [illustrationsDraft, setIllustrationsDraft] = useState([]);
  const [regenIllusBusy, setRegenIllusBusy] = useState(false);
  const [regenIllusError, setRegenIllusError] = useState(null);
  // storage_path → signed URL for inline preview (admin은 cross-device이므로
  // localStorage 같은 디바이스-로컬 캐시를 쓰지 않는다)
  const [illusUrls, setIllusUrls] = useState({});

  // 영상 element에 직접 닿아서 현재 재생 시각을 읽으려고 둔다.
  // ('이 순간 핀하기' 버튼이 이 ref를 통해 v.currentTime을 가져온다)
  const videoRef = useRef(null);

  // Load case + mark analyzing on first open
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getAdminCaseDetail(caseId);
      if (!d) {
        setError("케이스를 찾을 수 없어요.");
        return;
      }
      setDetail(d);
      setSymptoms(d.observations?.symptoms ?? {});
      setNotes(d.observations?.notes ?? "");

      // Auto-transition received → analyzing on open so the parent sees progress
      if (d.status === "received" && operatorId) {
        try {
          await markCaseAnalyzing({ caseId, operatorId });
          setDetail((prev) => prev ? { ...prev, status: "analyzing", operator_id: operatorId } : prev);
        } catch (err) {
          console.error("markCaseAnalyzing failed:", err);
        }
      }

      // If research row already exists (case was researched before), reflect it
      if (d.research?.status === "done") {
        setAiStatus("done");
        setPapersFound((d.research.papers ?? []).length);
        setLetterDraft(d.research.letter_draft ?? "");
        setLetterDirty(false);
        setPapersDraft(d.research.papers ?? []);
        setPapersDirty(false);
        setRecsDraft(d.research.recommendations ?? []);
        setRecsDirty(false);
        setObsDraft(d.research.observations ?? []);
        setObsHeadline("");
        setObsDirty(false);
        setIllustrationsDraft(d.research.illustrations ?? []);
        setRegenIllusError(null);
        // Stale if observations were edited after the research completed
        const obsAt = d.observations?.updated_at ? new Date(d.observations.updated_at).getTime() : 0;
        const resAt = d.research?.completed_at
          ? new Date(d.research.completed_at).getTime()
          : (d.research?.created_at ? new Date(d.research.created_at).getTime() : 0);
        setInputsStale(obsAt > resAt);
      } else {
        setInputsStale(false);
      }
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, [caseId, operatorId]);

  useEffect(() => { load(); }, [load]);

  // Debounced save of observations whenever symptoms/notes change
  const debounceTimer = useRef(null);
  useEffect(() => {
    if (loading || !detail) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await upsertObservations({ caseId, symptoms, notes });
        setLastSavedAt(Date.now());
        if (aiStatus === "done") setInputsStale(true);
      } catch (err) {
        console.error("observations save failed:", err);
      } finally {
        setSaving(false);
      }
    }, 600);
    return () => clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symptoms, notes]);

  // Two-phase thumbnail loading:
  //  1. existing thumbnails → fetch signed URLs in parallel
  //  2. videos that pre-date the thumbnail feature → extract+upload in series
  //     so we don't choke admin's bandwidth on 6 simultaneous downloads.
  useEffect(() => {
    const list = detail?.videos ?? [];
    if (!list.length) { setThumbUrls({}); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(list.map(async (v) => {
        if (!v.thumbnail_path) return;
        try {
          const url = await getSignedVideoUrl(v.thumbnail_path);
          map[v.id] = url;
        } catch (e) { console.warn("thumb signed-url failed:", v.id, e); }
      }));
      if (cancelled) return;
      setThumbUrls(map);

      const missing = list.filter((v) => !v.thumbnail_path);
      console.info("thumb: existing=%d, backfill=%d", list.length - missing.length, missing.length);
      for (const v of missing) {
        if (cancelled) return;
        try {
          const signed = await backfillVideoThumbnail(v);
          if (cancelled) return;
          if (signed) {
            setThumbUrls((prev) => ({ ...prev, [v.id]: signed }));
          }
        } catch (e) { console.warn("thumb backfill failed:", v.id, e); }
      }
    })();
    return () => { cancelled = true; };
  }, [detail?.videos]);

  // Resolve signed URLs for illustrations (admin은 cross-device이므로
  // localStorage 캐시 없이 매번 1시간짜리 signed URL을 받는다)
  useEffect(() => {
    if (!illustrationsDraft.length) { setIllusUrls({}); return; }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(illustrationsDraft.map(async (r) => {
        if (!r?.storage_path) return;
        try {
          const url = await getSignedVideoUrl(r.storage_path);
          next[r.storage_path] = url;
        } catch { /* leave missing — UI shows placeholder */ }
      }));
      if (!cancelled) setIllusUrls(next);
    })();
    return () => { cancelled = true; };
  }, [illustrationsDraft]);

  // Resolve signed URL whenever the active video changes
  const activeVideo = detail?.videos?.[activeVideoIdx] ?? null;
  useEffect(() => {
    setSignedUrl(null);
    setVideoError(null);
    if (!activeVideo?.storage_path) return;
    let cancelled = false;
    getSignedVideoUrl(activeVideo.storage_path)
      .then((url) => { if (!cancelled) setSignedUrl(url); })
      .catch((err) => { if (!cancelled) setVideoError(err.message ?? "영상 URL 발급 실패"); });
    return () => { cancelled = true; };
  }, [activeVideo?.storage_path]);

  const [aiError, setAiError] = useState(null);

  const runAI = async () => {
    if (!detail) return;
    setAiStatus("running");
    setPapersFound(0);
    setAiError(null);
    // Cosmetic step animation — Claude usually takes 10-30 seconds so we
    // bump 3 stages slowly instead of instantly completing.
    const stepTimer = setInterval(() => {
      setPapersFound((n) => (n < 3 ? n + 1 : n));
    }, 4500);
    try {
      await runResearch({ caseId });
      // Reload to pick up status='reviewing' and the it_research row
      const d = await getAdminCaseDetail(caseId);
      if (d) setDetail(d);
      setPapersFound((d?.research?.papers?.length) ?? 3);
      setLetterDraft(d?.research?.letter_draft ?? "");
      setLetterDirty(false);
      setPapersDraft(d?.research?.papers ?? []);
      setPapersDirty(false);
      setRecsDraft(d?.research?.recommendations ?? []);
      setRecsDirty(false);
      // AI는 research-case.ts에서 observations: []로 비워서 insert한다.
      // 그 빈 값으로 운영자가 핀해 둔 관찰을 덮어쓰지 않도록, 새 research가
      // 실제 데이터를 갖고 있을 때만 교체.
      const aiObs = d?.research?.observations ?? [];
      if (aiObs.length > 0) setObsDraft(aiObs);
      setObsHeadline("");
      setIllustrationsDraft(d?.research?.illustrations ?? []);
      setRegenIllusError(null);
      setObsDirty(false);
      setAiStatus("done");
      setInputsStale(false);
    } catch (err) {
      const msg = err?.message ?? "unknown";
      setAiError(msg);
      setAiStatus("idle");
      // Reload so status reflects the rollback to 'analyzing'
      try {
        const d = await getAdminCaseDetail(caseId);
        if (d) setDetail(d);
      } catch { /* ignore */ }
    } finally {
      clearInterval(stepTimer);
    }
  };

  const submit = async () => {
    if (!detail || !detail.research) return;
    const finalLetter = (letterDraft || "").trim()
      || (detail.research.letter_draft || "").trim()
      || "안녕하세요. 보내주신 영상을 차분히 보았어요.";
    const anyDirty = letterDirty || papersDirty || recsDirty || obsDirty;
    if (anyDirty) {
      const ok = window.confirm(
        "편집한 내용으로 전송할까요?\n\n전송 후엔 보호자에게 알림이 가요."
      );
      if (!ok) return;
    }
    setFinalizing(true);
    try {
      const content = {
        symptoms,
        observations: obsDraft,
        observation_headline: obsHeadline || undefined,
        papers: papersDraft,
        recommendations: recsDraft,
        // 부모 화면에서 letter 단락 사이에 끼울 인포그래픽. it_reports에 같이
        // 박아두면 admin이 더 이상 안 켜져 있어도 부모는 그대로 본다.
        illustrations: illustrationsDraft,
      };
      await finalizeReport({ caseId, operatorId, letter: finalLetter, content });
      onSubmit();
    } catch (err) {
      alert("전송 실패: " + (err.message ?? "unknown"));
      setFinalizing(false);
    }
  };

  // 현재 영상의 재생 시각을 mm:ss로 잡아 관찰 항목에 빈 row를 하나 추가.
  // 운영자는 ObservationsEditor에서 내용·tone·tag를 마저 채우면 된다.
  const pinCurrentMoment = () => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(v.currentTime)) return;
    const total = Math.max(0, Math.floor(v.currentTime));
    const m = Math.floor(total / 60);
    const s = total % 60;
    const ts = `${m}:${String(s).padStart(2, "0")}`;
    setObsDraft((prev) => [...prev, { ts, t: "", tag: "", tone: "" }]);
    setObsDirty(true);
  };

  const regenIllustrations = async () => {
    if (regenIllusBusy) return;
    if (!window.confirm("기존 인포그래픽을 모두 지우고 다시 생성할까요?\n편지·논문·권유는 그대로 유지돼요.")) return;
    setRegenIllusBusy(true);
    setRegenIllusError(null);
    try {
      await regenerateIllustrations({ caseId });
      const d = await getAdminCaseDetail(caseId);
      if (d) {
        setDetail(d);
        setIllustrationsDraft(d.research?.illustrations ?? []);
      }
    } catch (err) {
      setRegenIllusError(err.message ?? "재생성 실패");
    } finally {
      setRegenIllusBusy(false);
    }
  };

  const child = detail?.child;
  const childLine = child
    ? `${child.name}${formatAge(child.birth_month) ? ` · ${formatAge(child.birth_month)}` : ""}`
    : "아이 정보 없음";
  const statusLabel = STATUS_LABEL[detail?.status] ?? detail?.status ?? "";
  const videos = detail?.videos ?? [];
  const videoColorsA = ["#2c5f8d", "#e17055", "#78c1c8", "#e8a87c", "#9b89b3", "#5b7b9a"];
  const videoColorsB = ["#3a7ab5", "#e8a87c", "#00b894", "#ff9933", "#6c5ce7", "#2c5f8d"];

  const savedHint = useMemo(() => {
    if (saving) return "저장 중...";
    if (!lastSavedAt) return null;
    const mins = Math.floor((Date.now() - lastSavedAt) / 60000);
    return mins < 1 ? "방금 저장됨" : `${mins}분 전 저장됨`;
  }, [saving, lastSavedAt]);

  if (loading) {
    return (
      <div className="admin" style={{alignItems:'center',justifyContent:'center',display:'flex'}}>
        <div className="spinner"/>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="admin" style={{display:'flex',alignItems:'center',justifyContent:'center',padding:40}}>
        <div style={{textAlign:'center',fontFamily:'var(--font-sans)'}}>
          <div style={{fontSize:48,marginBottom:14}}>⚠️</div>
          <div style={{fontSize:18,fontWeight:800,marginBottom:8}}>{error ?? "케이스를 불러오지 못했어요"}</div>
          <button className="btn btn-outline" onClick={onBack} style={{marginTop:10,padding:'8px 18px'}}>
            ← 분석 큐로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <aside className="admin-side">
        <div className="brand">
          <span className="mark">📖</span>i-talk{" "}
          <span style={{fontWeight:500,color:'rgba(255,255,255,.5)',fontSize:11,marginLeft:4}}>ops</span>
        </div>
        <nav>
          <a onClick={onBack} style={{cursor:'pointer'}}>← 분석 큐</a>
          <a className="active">
            ✍️ {shortId(detail.id)} · {child?.name ?? ""}
          </a>
        </nav>
        <div style={{padding:'14px 22px',borderTop:'1px solid rgba(255,255,255,.08)'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <img src="/assets/character_doctor.png" alt="" style={{width:32,height:32,borderRadius:16,objectFit:'cover'}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:700}}>짱샘</div>
              <div style={{fontSize:10,color:'rgba(255,255,255,.5)'}}>소아물리치료사</div>
            </div>
            {onSignOut && (
              <button onClick={onSignOut}
                title="로그아웃"
                style={{
                  background:'transparent',border:'1px solid rgba(255,255,255,.15)',
                  color:'rgba(255,255,255,.6)',borderRadius:8,
                  padding:'6px 8px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
                }}>
                ↩
              </button>
            )}
          </div>
        </div>
      </aside>

      <main className="admin-main" style={{paddingRight:28}}>
        <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:4,flexWrap:'wrap'}}>
          <h1 className="admin-h1" style={{margin:0}}>{childLine}</h1>
          <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#e74c3c22',color:'#c0392b'}}>
            {statusLabel}
          </span>
          <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#2c5f8d14',color:'#2c5f8d'}}>
            Opus 4.7
          </span>
        </div>
        <p className="admin-sub">
          {detail.concern_tags?.length ? `증상: ${detail.concern_tags.join(", ")}` : "증상 미지정"}
          {detail.concern_memo ? ` · 어머니 메모: "${detail.concern_memo}"` : ""}
        </p>

        <div className="ws">
          <div className="pane">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:800,color:'#1a1a2e'}}>🎬 영상 {videos.length}개</div>
              <div style={{fontSize:11,color:'#718096'}}>
                {videos.reduce((acc, v) => acc + (v.duration_seconds ?? 0), 0)}초 · 총 {videos.length}편
              </div>
            </div>

            <div style={{
              aspectRatio: playerAspect.ratio,
              maxWidth: `calc(70vh * ${playerAspect.value})`,
              marginInline: 'auto',
              background:'linear-gradient(135deg,#1a1a2e,#2c5f8d)',
              borderRadius:12,position:'relative',overflow:'hidden',marginBottom:10,
            }}>
              {signedUrl ? (
                <video
                  ref={videoRef}
                  key={signedUrl}
                  src={signedUrl}
                  controls
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => {
                    const el = e.currentTarget;
                    if (el.videoWidth && el.videoHeight) {
                      setPlayerAspect({
                        ratio: `${el.videoWidth} / ${el.videoHeight}`,
                        value: el.videoWidth / el.videoHeight,
                      });
                    }
                  }}
                  style={{width:'100%',height:'100%',objectFit:'contain',background:'#000'}}
                />
              ) : videoError ? (
                <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',flexDirection:'column',gap:6,fontSize:13}}>
                  <span style={{fontSize:32}}>⚠️</span>
                  <span>{videoError}</span>
                </div>
              ) : videos.length === 0 ? (
                <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',opacity:.7}}>
                  업로드된 영상이 없어요
                </div>
              ) : (
                <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                  <div className="spinner"/>
                </div>
              )}
              {activeVideo && (
                <div style={{position:'absolute',top:10,left:10,background:'rgba(0,0,0,.5)',color:'#fff',padding:'3px 8px',borderRadius:4,fontSize:11,fontWeight:700}}>
                  #{activeVideo.position} · {formatDuration(activeVideo.duration_seconds)}
                </div>
              )}
            </div>

            {videos.length > 0 && (
              <div className="video-strip">
                {videos.map((v, idx) => {
                  const colorA = videoColorsA[(v.position - 1) % 6];
                  const colorB = videoColorsB[(v.position - 1) % 6];
                  const thumb = thumbUrls[v.id];
                  const baseStyle = thumb
                    ? { backgroundImage:`url(${thumb})`, backgroundSize:'cover', backgroundPosition:'center', backgroundColor:'#000' }
                    : { background:`linear-gradient(135deg, ${colorA}, ${colorB})` };
                  return (
                    <div
                      key={v.id}
                      className={"thumb" + (activeVideoIdx === idx ? " active" : "")}
                      onClick={() => setActiveVideoIdx(idx)}
                      style={{...baseStyle, textShadow:'0 1px 3px rgba(0,0,0,.6)'}}
                    >
                      #{v.position}
                      <div style={{position:'absolute',bottom:4,right:4,background:'rgba(0,0,0,.55)',padding:'1px 5px',borderRadius:3,fontSize:9,textShadow:'none'}}>
                        {formatDuration(v.duration_seconds)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeVideo && (
              <div style={{marginTop:18}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#2c5f8d',textTransform:'uppercase',letterSpacing:.4}}>
                    영상 #{activeVideo.position} · 관찰 메모
                  </div>
                  <button
                    type="button"
                    onClick={pinCurrentMoment}
                    style={{
                      background:'#2c5f8d',color:'#fff',border:'none',
                      borderRadius:9999,padding:'6px 12px',
                      fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
                    }}>
                    📌 이 순간 핀하기
                  </button>
                </div>
                <div style={{fontSize:10,color:'#718096',lineHeight:1.5}}>
                  버튼을 누르면 현재 재생 시각이 우측 "관찰 요약" 카드에 새 행으로 추가돼요. 내용은 우측에서 마저 적어주세요.
                </div>
              </div>
            )}
          </div>

          <div className="pane" style={{display:'flex',flexDirection:'column',gap:16}}>
            <SimilarCases caseId={caseId} title="🔗 비슷한 발행 케이스 (과거 사례 참고)"/>

            {inputsStale && aiStatus === "done" && (
              <div style={{
                background:'#fff7ed',border:'1px solid #fdba74',borderRadius:10,
                padding:'10px 12px',fontSize:12,color:'#9a3412',lineHeight:1.55,
                display:'flex',gap:8,alignItems:'flex-start',
              }}>
                <div style={{fontSize:14,lineHeight:1}}>⚠️</div>
                <div>
                  <b>체크리스트·관찰 메모가 변경되었어요.</b><br/>
                  변경 내용은 아직 리포트(편지·논문·권유)에 반영되지 않았습니다.
                  아래 <b>✨ 리서치 시작</b>(또는 다시 실행)을 눌러 AI 리서치를 다시 돌려야
                  부모에게 전송될 리포트에 반영됩니다.
                </div>
              </div>
            )}
            <div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:800,color:'#1a1a2e'}}>🩺 증상 체크리스트</div>
                {savedHint && (
                  <div style={{fontSize:10,color:'#718096'}}>{savedHint}</div>
                )}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {SYMPTOM_GROUPS.map((g) => (
                  <div key={g.cat}>
                    <div style={{fontSize:11,fontWeight:700,color:'#718096',marginBottom:4,letterSpacing:.2}}>
                      {g.cat}
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                      {g.items.map((s) => (
                        <label key={s.k} className={"sym-chip" + (symptoms[s.k] ? " on" : "")}>
                          <input
                            type="checkbox"
                            checked={!!symptoms[s.k]}
                            onChange={() => setSymptoms((prev) => ({ ...prev, [s.k]: !prev[s.k] }))}
                          />
                          {s.l}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{fontSize:13,fontWeight:800,color:'#1a1a2e',marginBottom:8}}>✍️ 운영진 관찰 메모</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="영상별 관찰 내용 · 권유 사항 · 리서치에 참고할 키워드 등"
                style={{width:'100%',minHeight:150,padding:12,borderRadius:10,border:'1px solid #e2e8f0',
                  fontSize:13,fontFamily:'inherit',lineHeight:1.6,resize:'vertical'}}
              />
            </div>

            <div className="ai-panel">
              <div className="ai-h">
                🤖 AI 리서치 보강{" "}
                <span style={{marginLeft:'auto',fontSize:10,color:'#718096',fontWeight:500}}>
                  Opus 4.7 · Anthropic API
                </span>
              </div>
              {aiStatus === "idle" && (
                <div>
                  <div style={{fontSize:12,color:'#4a5568',lineHeight:1.6,marginBottom:10}}>
                    체크된 증상과 메모를 Claude Opus에 전달해,
                    관련 논문 3편과 짱샘 톤의 편지 초안을 받아옵니다.
                  </div>
                  <button className="btn btn-primary" style={{padding:'10px 18px',fontSize:12}} onClick={runAI}>
                    ✨ 리서치 시작
                  </button>
                  {aiError && (
                    <div style={{marginTop:10,padding:'10px 12px',background:'#e74c3c15',color:'#c0392b',borderRadius:10,fontSize:11,lineHeight:1.5}}>
                      ❌ {aiError}
                    </div>
                  )}
                </div>
              )}
              {aiStatus !== "idle" && (
                <div>
                  <div className="ai-step">
                    <span className={aiStatus === "done" ? "mark-ok" : "mark-run"}>{aiStatus === "done" ? "✓" : "•"}</span>
                    <div>
                      <b>증상 키워드 추출</b>
                      <div style={{fontSize:11,color:'#718096'}}>
                        {Object.entries(symptoms).filter(([, v]) => v).map(([k]) => SYMPTOM_LABEL_MAP[k]).filter(Boolean).join(", ") || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="ai-step">
                    <span className={aiStatus === "done" ? "mark-ok" : papersFound > 0 ? "mark-ok" : "mark-run"}>
                      {aiStatus === "done" ? "✓" : (papersFound > 0 ? "✓" : "⋯")}
                    </span>
                    <div>
                      <b>PubMed · Google Scholar 검색</b>
                      <div style={{fontSize:11,color:'#718096'}}>{papersFound}편 관련 논문 확보</div>
                    </div>
                  </div>
                  <div className="ai-step">
                    <span className={aiStatus === "done" ? "mark-ok" : "mark-run"}>{aiStatus === "done" ? "✓" : "⋯"}</span>
                    <div>
                      <b>핵심 인용 추출 및 한글 요약</b>
                      {aiStatus === "done" && (
                        <div style={{fontSize:11,color:'#718096'}}>3편 · 편지체 톤으로 정리 완료</div>
                      )}
                    </div>
                  </div>
                  <div className="ai-step">
                    <span className={aiStatus === "done" ? "mark-ok" : "mark-run"}>{aiStatus === "done" ? "✓" : "⋯"}</span>
                    <div>
                      <b>편지 초안 생성 (짱샘 톤)</b>
                    </div>
                  </div>
                  {aiStatus === "done" && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:'1px dashed #e2e8f0',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      <button
                        className="btn btn-outline"
                        style={{padding:'8px 14px',fontSize:11}}
                        onClick={() => {
                          if (!inputsStale && !window.confirm("기존 AI 결과를 버리고 다시 실행할까요?\n편집한 편지/논문/권유는 초기화됩니다.")) return;
                          runAI();
                        }}
                      >
                        🔄 리서치 다시 실행
                      </button>
                      {inputsStale && (
                        <span style={{fontSize:11,color:'#9a3412',fontWeight:700}}>
                          체크리스트·메모 변경분 반영 필요
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {aiStatus !== "idle" && (
              <div style={aiStatus === "running" ? {opacity:0.4,pointerEvents:'none'} : undefined}>
              <PapersEditor
                papers={papersDraft}
                dirty={papersDirty}
                onChange={(next) => { setPapersDraft(next); setPapersDirty(true); }}
                onReset={() => {
                  if (papersDirty && !window.confirm("편집한 논문을 AI 초안으로 되돌릴까요?")) return;
                  setPapersDraft(detail.research?.papers ?? []);
                  setPapersDirty(false);
                }}
              />
              </div>
            )}

            {aiStatus !== "idle" && (
              <div style={aiStatus === "running" ? {opacity:0.4,pointerEvents:'none'} : undefined}>
              <RecommendationsEditor
                recs={recsDraft}
                dirty={recsDirty}
                onChange={(next) => { setRecsDraft(next); setRecsDirty(true); }}
                onReset={() => {
                  if (recsDirty && !window.confirm("편집한 권유를 AI 초안으로 되돌릴까요?")) return;
                  setRecsDraft(detail.research?.recommendations ?? []);
                  setRecsDirty(false);
                }}
              />
              </div>
            )}

            {/* ObservationsEditor는 다른 에디터와 달리 AI 출력에 의존하지 않는다.
                운영자가 영상 보면서 📌 핀으로 관찰을 누적하는 게 자연스러운 흐름이라
                aiStatus와 무관하게 항상 노출. */}
            <ObservationsEditor
              headline={obsHeadline}
              items={obsDraft}
              dirty={obsDirty}
              onHeadlineChange={(v) => { setObsHeadline(v); setObsDirty(true); }}
              onItemsChange={(next) => { setObsDraft(next); setObsDirty(true); }}
              onReset={() => {
                if (obsDirty && !window.confirm("편집한 관찰을 비울까요?")) return;
                setObsDraft(detail.research?.observations ?? []);
                setObsHeadline("");
                setObsDirty(false);
              }}
            />

            {aiStatus !== "idle" && (
              <div style={{background:'#fff',border:'1px solid #2c5f8d33',borderRadius:12,padding:14,
                ...(aiStatus === "running" ? {opacity:0.4,pointerEvents:'none'} : {})}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:800,color:'#1a1a2e'}}>
                    💌 보호자에게 보낼 편지 (편집 가능)
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    {letterDirty && (
                      <span style={{fontSize:10,color:'#e6852b',fontWeight:700}}>● 수정됨</span>
                    )}
                    <button
                      onClick={() => {
                        if (letterDirty && !window.confirm("편집한 내용을 버리고 AI 초안으로 되돌릴까요?")) return;
                        setLetterDraft(detail.research.letter_draft ?? "");
                        setLetterDirty(false);
                      }}
                      style={{
                        background:'transparent',border:'1px solid #e2e8f0',
                        color:'#718096',borderRadius:6,padding:'4px 10px',
                        fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
                      }}>
                      AI 초안으로 되돌리기
                    </button>
                  </div>
                </div>
                <textarea
                  value={letterDraft}
                  onChange={(e) => { setLetterDraft(e.target.value); setLetterDirty(true); }}
                  placeholder="AI가 만든 편지 초안이 여기에 표시됩니다. 짱샘 톤으로 자유롭게 다듬어주세요."
                  style={{
                    width:'100%',minHeight:240,padding:12,borderRadius:10,
                    border:'1px solid #e2e8f0',fontSize:13,fontFamily:'inherit',
                    lineHeight:1.7,resize:'vertical',whiteSpace:'pre-wrap',
                  }}
                />
                <div style={{marginTop:6,fontSize:10,color:'#718096',lineHeight:1.5}}>
                  · 줄 사이를 빈 줄(\n\n)로 띄우면 보호자 화면에서 문단 구분이 자동으로 적용돼요.<br/>
                  · 전송 후에는 보호자가 즉시 알림을 받습니다. 한 번 더 검토 후 전송하세요.
                </div>
              </div>
            )}

            {aiStatus !== "idle" && (
              <div style={{background:'#fff',border:'1px solid #2c5f8d33',borderRadius:12,padding:14,
                ...(aiStatus === "running" ? {opacity:0.4,pointerEvents:'none'} : {})}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:8}}>
                  <div style={{fontSize:12,fontWeight:800,color:'#1a1a2e'}}>
                    🎨 편지 사이에 들어갈 인포그래픽 ({illustrationsDraft.length}장)
                  </div>
                  <button
                    onClick={regenIllustrations}
                    disabled={regenIllusBusy || illustrationsDraft.length === 0}
                    style={{
                      background:'transparent',border:'1px solid #e2e8f0',
                      color: regenIllusBusy ? '#a0aec0' : '#2c5f8d',
                      borderRadius:6,padding:'4px 10px',
                      fontSize:10,fontWeight:700,
                      cursor:(regenIllusBusy || illustrationsDraft.length===0) ? 'not-allowed' : 'pointer',
                      fontFamily:'inherit',opacity:illustrationsDraft.length===0?0.4:1,
                    }}>
                    {regenIllusBusy ? '재생성 중...' : '🔄 인포그래픽 재생성'}
                  </button>
                </div>

                {illustrationsDraft.length === 0 && (
                  <div style={{fontSize:11,color:'#9a3412',lineHeight:1.6}}>
                    저장된 인포그래픽이 없어요. 첫 리서치에서 Gemini가 실패했거나
                    GEMINI_API_KEY가 설정되지 않았을 수 있어요. 환경 변수 확인 후
                    "리서치 다시 실행"으로 처음부터 만들어 주세요.
                  </div>
                )}

                {illustrationsDraft.length > 0 && (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:8}}>
                    {illustrationsDraft.map((r, i) => {
                      const url = r.storage_path ? illusUrls[r.storage_path] : null;
                      return (
                        <div key={i} style={{background:'#fafafa',borderRadius:8,overflow:'hidden',
                          border:'1px solid #e2e8f0',display:'flex',flexDirection:'column'}}>
                          <div style={{aspectRatio:'16/9',background:'#f1f5f9',position:'relative'}}>
                            {url ? (
                              <img src={url} alt={r.alt ?? ''}
                                style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}/>
                            ) : (
                              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',
                                justifyContent:'center',fontSize:10,color:'#a0aec0'}}>
                                불러오는 중...
                              </div>
                            )}
                            <div style={{position:'absolute',top:4,left:4,background:'rgba(0,0,0,.55)',
                              color:'#fff',fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:6}}>
                              ¶{r.after_paragraph} 뒤
                            </div>
                          </div>
                          <div style={{padding:'6px 8px',fontSize:10,color:'#4a5568',lineHeight:1.4}}>
                            {r.alt}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {regenIllusError && (
                  <div style={{marginTop:8,fontSize:11,color:'#c53030',lineHeight:1.5}}>
                    재생성 실패: {regenIllusError}
                  </div>
                )}

                <div style={{marginTop:8,fontSize:10,color:'#718096',lineHeight:1.5}}>
                  · 그림은 letter 단락 사이에 자동으로 끼워집니다 (¶N 뒤 = N번째 단락 다음).<br/>
                  · 톤이 어긋나면 "재생성"으로 다시 굴려 보세요. 같은 프롬프트를 다시 호출합니다.
                </div>
              </div>
            )}

            <div style={{display:'flex',gap:8,marginTop:'auto',flexWrap:'wrap'}}>
              <button className="btn btn-outline" style={{flex:'1 1 110px',padding:'12px'}} onClick={load}>
                ⟳ 새로고침
              </button>
              <button
                onClick={() => setPreviewOpen(true)}
                disabled={aiStatus !== "done"}
                style={{
                  flex:'1 1 130px',padding:'12px',fontSize:13,
                  opacity:aiStatus === "done" ? 1 : 0.4,
                  background:'#fff',color:'#2c5f8d',
                  border:'1px solid #2c5f8d',borderRadius:9999,fontWeight:700,
                  cursor:aiStatus === "done" ? 'pointer' : 'not-allowed',
                  fontFamily:'inherit',
                }}
              >
                👁 부모 화면 미리보기
              </button>
              <button
                onClick={submit}
                disabled={aiStatus !== "done" || finalizing}
                style={{
                  flex:'1 1 160px',padding:'12px',fontSize:13,
                  opacity:(aiStatus === "done" && !finalizing) ? 1 : 0.4,
                  background:(aiStatus === "done" && !finalizing) ? '#ff9933' : '#ccc',
                  color:'#fff',border:'none',borderRadius:9999,fontWeight:700,
                  cursor:(aiStatus === "done" && !finalizing) ? 'pointer' : 'not-allowed',
                  fontFamily:'inherit',
                }}
              >
                {finalizing ? "전송 중..." : "짱샘 검토 완료 · 전송 →"}
              </button>
            </div>
          </div>
        </div>
      </main>

      {previewOpen && (
        <ReportPreviewModal
          caseId={caseId}
          detail={detail}
          letterDraft={letterDraft}
          obsDraft={obsDraft}
          obsHeadline={obsHeadline}
          papersDraft={papersDraft}
          recsDraft={recsDraft}
          illustrationsDraft={illustrationsDraft}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Report preview modal — phone mockup wrapping ReportView with draft data
// ────────────────────────────────────────────────────────────────────

function ReportPreviewModal({
  caseId,
  detail,
  letterDraft,
  obsDraft,
  obsHeadline,
  papersDraft,
  recsDraft,
  illustrationsDraft,
  onClose,
}) {
  // Hide phone frame on small admin screens (already mobile)
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );
  useEffect(() => {
    const handle = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const previewCaseRecord = detail
    ? {
        id: caseId,
        concern_tags: detail.concern_tags ?? [],
        it_children: detail.child ? { name: detail.child.name } : null,
      }
    : null;

  const previewReport = {
    case_id: caseId,
    letter:
      (letterDraft || "").trim()
      || (detail?.research?.letter_draft || "").trim()
      || "",
    content: {
      observations: obsDraft,
      observation_headline: obsHeadline || undefined,
      papers: papersDraft,
      recommendations: recsDraft,
      illustrations: illustrationsDraft ?? [],
    },
    finalized_at: detail?.research?.created_at ?? new Date().toISOString(),
    viewed_at: null,
  };

  const overlayStyle = {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(15, 23, 42, 0.72)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: isNarrow ? 0 : 24,
  };

  const phoneFrameStyle = isNarrow
    ? {
        width: "100%", height: "100%",
        background: "#fffbf5", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }
    : {
        width: 393, height: 844,
        background: "#fffbf5", overflow: "hidden",
        borderRadius: 44, border: "12px solid #1a1a2e",
        boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column",
        position: "relative",
      };

  return (
    <div style={overlayStyle} onClick={onClose}>
      {!isNarrow && (
        <div style={{
          position: "absolute", top: 18, left: 24, color: "#fff",
          fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
        }}>
          👁 부모가 볼 화면 (미리보기 — 저장되지 않음)
        </div>
      )}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 14, right: 18, zIndex: 1,
          background: "rgba(255,255,255,0.15)", color: "#fff",
          border: "1px solid rgba(255,255,255,0.3)", borderRadius: 9999,
          padding: "8px 16px", fontSize: 12, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
        }}
      >
        닫기 ✕
      </button>

      <div style={phoneFrameStyle} onClick={(e) => e.stopPropagation()}>
        {!isNarrow && (
          <div style={{
            height: 28, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
          }}>
            <div style={{
              width: 110, height: 22, background: "#1a1a2e",
              borderRadius: "0 0 14px 14px",
            }}/>
          </div>
        )}
        <div style={{
          flex: 1, overflowY: "auto", overflowX: "hidden",
          background: "#fffbf5",
          paddingBottom: 30,
        }}>
          <div style={{padding: "14px 8px 10px"}}>
            <div style={{fontSize:13,fontWeight:800,padding:"0 12px"}}>
              💌 {previewCaseRecord?.concern_tags?.[0]
                ? `${previewCaseRecord.concern_tags[0]} 관찰 리포트`
                : `${previewCaseRecord?.it_children?.name ?? "아이"} 리포트`}
            </div>
          </div>
          <ReportView
            caseRecord={previewCaseRecord}
            report={previewReport}
            loading={false}
          />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// D-2 Editors — papers / recommendations / observations
// ────────────────────────────────────────────────────────────────────

function EditorCard({ title, accent, dirty, onReset, children }) {
  return (
    <div style={{background:'#fff',border:`1px solid ${accent}33`,borderRadius:12,padding:14}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:800,color:'#1a1a2e'}}>{title}</div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {dirty && <span style={{fontSize:10,color:'#e6852b',fontWeight:700}}>● 수정됨</span>}
          <button
            onClick={onReset}
            style={{
              background:'transparent',border:'1px solid #e2e8f0',
              color:'#718096',borderRadius:6,padding:'4px 10px',
              fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
            }}>
            AI 초안으로 되돌리기
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

const editInputStyle = {
  width:'100%',padding:'8px 10px',borderRadius:8,
  border:'1px solid #e2e8f0',fontSize:12,fontFamily:'inherit',
  lineHeight:1.5,resize:'vertical',
};

function PapersEditor({ papers, dirty, onChange, onReset }) {
  const update = (i, patch) => {
    const next = papers.map((p, idx) => idx === i ? { ...p, ...patch } : p);
    onChange(next);
  };
  return (
    <EditorCard title={`📚 확보한 논문 ${papers.length}편 (편집 가능)`} accent="#27ae60" dirty={dirty} onReset={onReset}>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {papers.map((p, i) => (
          <div key={i} style={{padding:10,background:'#f9fafb',borderRadius:10,border:'1px solid #e2e8f0'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#1f7a3f',marginBottom:8}}>📄 논문 #{i+1}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
              <input type="text" value={p.cover ?? ""} placeholder="저널 약칭 (J Pediatr 등)"
                onChange={(e) => update(i, { cover: e.target.value })} style={editInputStyle}/>
              <input type="text" value={p.year ?? ""} placeholder="연도 (2024)"
                onChange={(e) => update(i, { year: e.target.value })} style={editInputStyle}/>
            </div>
            <input type="text" value={p.title ?? ""} placeholder="논문 제목 (영문)"
              onChange={(e) => update(i, { title: e.target.value })} style={{...editInputStyle,marginBottom:8}}/>
            <input type="text" value={p.source ?? ""} placeholder="저널명 · 저자 et al. 연도"
              onChange={(e) => update(i, { source: e.target.value })} style={{...editInputStyle,marginBottom:8}}/>
            <textarea value={p.quote ?? ""} placeholder="한국어 요약 인용구 (2-3문장)"
              onChange={(e) => update(i, { quote: e.target.value })}
              style={{...editInputStyle,minHeight:60}}/>
          </div>
        ))}
      </div>
    </EditorCard>
  );
}

function RecommendationsEditor({ recs, dirty, onChange, onReset }) {
  const update = (i, patch) => {
    const next = recs.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    onChange(next);
  };
  return (
    <EditorCard title={`✨ 이번 달 권유 ${recs.length}가지 (편집 가능)`} accent="#ff9933" dirty={dirty} onReset={onReset}>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {recs.map((r, i) => (
          <div key={i} style={{padding:10,background:'#fffbf5',borderRadius:10,border:'1px solid #f0e8d8'}}>
            <div style={{display:'flex',gap:8,marginBottom:8}}>
              <input type="text" value={r.emoji ?? ""} placeholder="🌙"
                onChange={(e) => update(i, { emoji: e.target.value })}
                style={{...editInputStyle,width:60,textAlign:'center',fontSize:18}}/>
              <input type="text" value={r.title ?? ""} placeholder="권유 제목 (짧게)"
                onChange={(e) => update(i, { title: e.target.value })} style={editInputStyle}/>
            </div>
            <textarea value={r.detail ?? ""} placeholder="구체적인 방법 · 빈도"
              onChange={(e) => update(i, { detail: e.target.value })}
              style={{...editInputStyle,minHeight:50}}/>
          </div>
        ))}
      </div>
    </EditorCard>
  );
}

function ObservationsEditor({ headline, items, dirty, onHeadlineChange, onItemsChange, onReset }) {
  const update = (i, patch) => {
    const next = items.map((o, idx) => idx === i ? { ...o, ...patch } : o);
    onItemsChange(next);
  };
  const add = () => onItemsChange([...items, { ts: "", t: "", tag: "", tone: "" }]);
  const remove = (i) => onItemsChange(items.filter((_, idx) => idx !== i));
  return (
    <EditorCard title={`🔬 관찰 요약 ${items.length}개 · 직접 입력`} accent="#9b89b3" dirty={dirty} onReset={onReset}>
      <div style={{marginBottom:12}}>
        <div style={{fontSize:10,fontWeight:700,color:'#718096',marginBottom:4}}>핵심 관찰 한 줄</div>
        <input type="text" value={headline ?? ""} placeholder="예: 좌측 회피 + 모로반사 잔여 신호가 함께 보였어요"
          onChange={(e) => onHeadlineChange(e.target.value)} style={editInputStyle}/>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.map((o, i) => (
          <div key={i} style={{padding:10,background:'#f9f7fb',borderRadius:10,border:'1px solid #e8e3ef'}}>
            <div style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
              <input type="text" value={o.ts ?? ""} placeholder="0:23"
                onChange={(e) => update(i, { ts: e.target.value })}
                style={{...editInputStyle,width:80}}/>
              <select value={o.tone ?? ""}
                onChange={(e) => update(i, { tone: e.target.value })}
                style={{...editInputStyle,width:90}}>
                <option value="">기본</option>
                <option value="warm">따뜻함</option>
              </select>
              <input type="text" value={o.tag ?? ""} placeholder="태그 (선택)"
                onChange={(e) => update(i, { tag: e.target.value })}
                style={{...editInputStyle,flex:1}}/>
              <button onClick={() => remove(i)}
                style={{background:'transparent',border:'1px solid #e2e8f0',color:'#c0392b',
                  borderRadius:6,padding:'6px 10px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                삭제
              </button>
            </div>
            <textarea value={o.t ?? ""} placeholder="관찰 내용"
              onChange={(e) => update(i, { t: e.target.value })}
              style={{...editInputStyle,minHeight:50}}/>
          </div>
        ))}
        <button onClick={add}
          style={{padding:'10px',background:'#9b89b315',border:'1px dashed #9b89b3',
            color:'#7a6796',borderRadius:10,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
          + 관찰 추가
        </button>
      </div>
    </EditorCard>
  );
}
